import { useState, useEffect } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useMeetings } from '@/hooks/useMeetings';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Clock, MapPin, Video, Plus, CheckCircle, XCircle, AlertCircle, Sparkles, Users, Check, User, Mail, Copy, Loader2, Play, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useAppUsers } from '@/hooks/useAppUsers';
import { useSalesDevelopment } from '@/hooks/useSalesDevelopment';
import { useGoogleCalendar } from '@/hooks/useGoogleCalendar';
import { useAuth } from '@/hooks/useAuth';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { MeetingConfirmationModal } from '@/components/meetings/MeetingConfirmationModal';

interface MeetingsTabProps {
  dealId?: string;
  leadId?: string;
  contactId?: string;
}

const statusColors = {
  scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200',
  no_show: 'bg-orange-100 text-orange-800 border-orange-200',
  rescheduled: 'bg-yellow-100 text-yellow-800 border-yellow-200',
};

const statusIcons = {
  scheduled: Clock,
  completed: CheckCircle,
  cancelled: XCircle,
  no_show: AlertCircle,
  rescheduled: Calendar,
};

export function MeetingsTab({ dealId, leadId, contactId }: MeetingsTabProps) {
  const { meetings, loading, createMeeting, updateMeeting, completeMeeting, cancelMeeting, rescheduleMeeting, loadMeetings } = useMeetings(dealId, leadId, contactId);
  const { users } = useAppUsers();
  const { salesDevelopment } = useSalesDevelopment();
  const { user } = useAuth();
  const { isConnecting, isConnected, connectGoogleCalendar, checkConnection, checkAvailability, createCalendarEvent } = useGoogleCalendar();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendGoogleInvite, setSendGoogleInvite] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmedMeeting, setConfirmedMeeting] = useState<any>(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState<{
    checked: boolean;
    available: boolean;
    conflicts?: any[];
  } | null>(null);
  const [showConflicts, setShowConflicts] = useState(false);
  const [isHostConnected, setIsHostConnected] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [meetingToReschedule, setMeetingToReschedule] = useState<any | null>(null);
  const [expandedTranscripts, setExpandedTranscripts] = useState<Set<string>>(new Set());
  const [rescheduleFormData, setRescheduleFormData] = useState({
    scheduled_at: '',
    location: '',
    title: '',
    description: '',
  });
  
  // Combine all users (app_users + SDRs), remove duplicates, and sort alphabetically
  const allUsers = [...(users || []), ...(salesDevelopment || [])]
    .filter((user, index, self) => 
      index === self.findIndex((u) => u.id === user.id)
    )
    .sort((a, b) => {
      const nameA = (a.display_name || `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email || '').toLowerCase();
      const nameB = (b.display_name || `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.email || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    scheduled_at: '',
    location: '',
    meeting_url: '',
    created_by: '',
    attendees: [] as string[],
  });

  const [attendeeEmail, setAttendeeEmail] = useState('');
  const [ccEmail, setCcEmail] = useState('');

  useEffect(() => {
    const updateHostConnection = async () => {
      if (formData.created_by && user) {
        const connected = await checkConnection(formData.created_by);
        setIsHostConnected(connected);
      } else {
        setIsHostConnected(false);
      }
    };
    
    updateHostConnection();
    
    // Reset availability when host changes
    setAvailabilityStatus(null);
    setShowConflicts(false);
  }, [formData.created_by, user]);

  // Check availability when date, time, or host changes
  useEffect(() => {
    const checkHostAvailability = async () => {
      if (!formData.created_by || !formData.scheduled_at) {
        setAvailabilityStatus(null);
        return;
      }

      setCheckingAvailability(true);
      setAvailabilityStatus(null);
      setShowConflicts(false);

      try {
        const startTime = new Date(formData.scheduled_at);
        const endTime = new Date(startTime.getTime() + 60 * 60000); // Default 60 minutes

        const result = await checkAvailability({
          host_user_id: formData.created_by,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
        });

        if (result.success) {
          setAvailabilityStatus({
            checked: true,
            available: result.available,
            conflicts: result.conflicts || [],
          });
          setIsHostConnected(result.connected !== false);
        } else if (!result.connected) {
          // Host not connected, don't show error
          setIsHostConnected(false);
          setAvailabilityStatus(null);
        }
      } catch (error) {
        console.error('Error checking availability:', error);
        setAvailabilityStatus(null);
      } finally {
        setCheckingAvailability(false);
      }
    };

    const timeoutId = setTimeout(checkHostAvailability, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.created_by, formData.scheduled_at]);

  // Pre-populate attendees and CC when modal opens
  useEffect(() => {
    const loadInitialData = async () => {
      if (isDialogOpen) {
        const updates: Partial<typeof formData> = {};
        
        // Fetch contact email if contactId exists
        if (contactId) {
          const { data, error } = await supabase
            .from('contacts')
            .select('primary_email')
            .eq('id', contactId)
            .single();
          
          if (data?.primary_email) {
            updates.attendees = [data.primary_email];
          }
        }

        // Fetch current user email
        if (user) {
          const { data, error } = await supabase
            .from('app_users')
            .select('email')
            .eq('auth_user_id', user.id)
            .maybeSingle();
          
          if (data?.email) {
            updates.attendees = [...(updates.attendees || []), data.email];
          }
        }

        // Update form data once with all values
        if (Object.keys(updates).length > 0) {
          setFormData(prev => ({
            ...prev,
            ...updates
          }));
        }
      } else {
        // Reset form when modal closes
        setFormData({
          title: '',
          description: '',
          scheduled_at: '',
          location: '',
          meeting_url: '',
          created_by: '',
          attendees: [] as string[],
        });
        setAttendeeEmail('');
        setCcEmail('');
        setSendGoogleInvite(true);
        setAvailabilityStatus(null);
        setShowConflicts(false);
      }
    };

    loadInitialData();
  }, [isDialogOpen, contactId, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // 1. Create meeting in database
      const meeting = await createMeeting({
        ...formData,
        deal_id: dealId || null,
        lead_id: leadId || null,
        contact_id: contactId,
        status: 'scheduled',
      });

      // 2. If send Google invite is checked and host is connected
      let finalMeetingData = meeting;
      if (sendGoogleInvite && isHostConnected && meeting) {
        const startDate = new Date(formData.scheduled_at);
        const endDate = new Date(startDate.getTime() + 60 * 60000); // Default 60 minutes

        try {
          const result = await createCalendarEvent({
            host_user_id: formData.created_by,
            title: formData.title,
            description: formData.description,
            start_time: startDate.toISOString(),
            end_time: endDate.toISOString(),
            location: formData.location,
            attendees: formData.attendees,
            meeting_id: meeting.id,
          });

          if (result?.meet_link || result?.event_id) {
            // Update meeting with the Google Meet link and calendar event ID (skip reload and toast)
            await updateMeeting(meeting.id, {
              meeting_url: result.meet_link || undefined,
              google_calendar_event_id: result.event_id,
              google_calendar_html_link: result.event_link,
            }, { skipReload: true, skipToast: true });
            
            finalMeetingData = {
              ...meeting,
              meeting_url: result.meet_link || meeting.meeting_url,
              google_calendar_event_id: result.event_id,
              google_calendar_html_link: result.event_link,
            };
          }
        } catch (calError) {
          console.error('Calendar event error:', calError);
          toast.warning('Reunião criada mas falha no Google Calendar');
        }
      }

      // 3. Reload meetings once
      await loadMeetings();
      
      // 4. Show confirmation modal
      setConfirmedMeeting({
        title: formData.title,
        scheduled_at: formData.scheduled_at,
        meeting_url: finalMeetingData.meeting_url,
        attendees: formData.attendees,
      });
      setShowConfirmation(true);
    } catch (error) {
      console.error('Error creating meeting:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading meetings...</div>;
  }

  const upcomingMeetings = meetings.filter(m => m.status === 'scheduled' && new Date(m.scheduled_at) > new Date());
  const pastMeetings = meetings.filter(m => m.status !== 'scheduled' || new Date(m.scheduled_at) <= new Date());

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Meetings</h3>
        {(dealId || leadId || contactId) && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Schedule Meeting
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Schedule New Meeting</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pb-4">
              <div>
                <Label htmlFor="title">Meeting Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="scheduled_at">Date & Time *</Label>
                  <Input
                    id="scheduled_at"
                    type="datetime-local"
                    value={formData.scheduled_at}
                    onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="meeting_url">Meeting Link</Label>
                <Input
                  id="meeting_url"
                  type="url"
                  value={formData.meeting_url}
                  onChange={(e) => setFormData({ ...formData, meeting_url: e.target.value })}
                  placeholder="Zoom, Teams, ou outro link de reunião"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  💡 Dica: Se você enviar convite via Google Calendar, um link do Google Meet será gerado automaticamente.
                </p>
              </div>

              <div>
                <Label htmlFor="host">Host / Organizer *</Label>
                <Select
                  value={formData.created_by}
                  onValueChange={(value) => setFormData({ ...formData, created_by: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select host" />
                  </SelectTrigger>
                  <SelectContent>
                    {allUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.display_name || `${user.first_name} ${user.last_name}`.trim() || user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Host Not Connected Warning */}
              {formData.created_by && !isHostConnected && !checkingAvailability && (
                <div className="p-4 border border-orange-200 rounded-lg bg-orange-50/50">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-orange-900">
                        Host não conectou Google Calendar
                      </p>
                      <p className="text-xs text-orange-700 mt-1">
                        A disponibilidade real NÃO foi verificada. O host precisa conectar para verificar conflitos no calendário.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Attendees/Participants */}
              <div>
                <Label htmlFor="attendees">Participantes / Attendees</Label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      id="attendees"
                      type="email"
                      value={attendeeEmail}
                      onChange={(e) => setAttendeeEmail(e.target.value)}
                      placeholder="email@exemplo.com"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (attendeeEmail && !formData.attendees.includes(attendeeEmail)) {
                          setFormData({ ...formData, attendees: [...formData.attendees, attendeeEmail] });
                          setAttendeeEmail('');
                        }
                      }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {formData.attendees.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.attendees.map((email, index) => (
                        <Badge key={index} variant="secondary" className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {email}
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                attendees: formData.attendees.filter((_, i) => i !== index)
                              });
                            }}
                            className="ml-1 hover:text-destructive"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              {/* Availability Check */}
              {formData.created_by && formData.scheduled_at && isHostConnected && (
                <div className="space-y-2 p-4 border rounded-lg bg-muted/30">
                  {checkingAvailability && (
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                      <span>Verificando disponibilidade...</span>
                    </div>
                  )}

                  {availabilityStatus?.checked && !checkingAvailability && (
                    <div className="space-y-2">
                      {availabilityStatus.available ? (
                        <div className="flex items-center space-x-2 text-sm text-green-600">
                          <Check className="h-4 w-4" />
                          <span>Host está disponível neste horário</span>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between p-3 bg-destructive/10 rounded-md">
                            <div className="flex items-center space-x-2 text-sm text-destructive">
                              <AlertCircle className="h-4 w-4" />
                              <span>Host tem {availabilityStatus.conflicts?.length || 0} conflito(s) neste horário</span>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setShowConflicts(!showConflicts)}
                            >
                              {showConflicts ? 'Ocultar' : 'Ver agenda'}
                            </Button>
                          </div>

                          {showConflicts && availabilityStatus.conflicts && availabilityStatus.conflicts.length > 0 && (
                            <div className="space-y-2 pl-6">
                              <p className="text-sm font-medium">Eventos conflitantes:</p>
                              {availabilityStatus.conflicts.map((conflict, idx) => (
                                <div key={idx} className="text-sm p-2 bg-muted rounded">
                                  <p className="font-medium">{conflict.summary}</p>
                                  <p className="text-muted-foreground text-xs">
                                    {new Date(conflict.start).toLocaleString('pt-BR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })} - {new Date(conflict.end).toLocaleString('pt-BR', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </p>
                                  {conflict.htmlLink && (
                                    <a 
                                      href={conflict.htmlLink} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-xs text-primary hover:underline"
                                    >
                                      Ver no Google Calendar
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}


              {/* Google Calendar Integration */}
              {(formData.created_by === user?.id || isHostConnected) && (
                <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="google-invite"
                        checked={sendGoogleInvite}
                        onCheckedChange={(checked) => setSendGoogleInvite(checked as boolean)}
                        disabled={!formData.created_by}
                      />
                      <Label htmlFor="google-invite" className="cursor-pointer">
                        Enviar convite via Google Calendar
                      </Label>
                    </div>
                    
                    {formData.created_by && !isHostConnected && formData.created_by === user?.id && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => connectGoogleCalendar(formData.created_by)}
                        disabled={isConnecting}
                      >
                        {isConnecting ? 'Conectando...' : 'Conectar meu Google'}
                      </Button>
                    )}
                    
                    {formData.created_by && !isHostConnected && formData.created_by !== user?.id && (
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-orange-600" />
                        <span className="text-sm text-orange-700">
                          {allUsers.find(u => u.id === formData.created_by)?.display_name || 'Host'} precisa conectar o Google Calendar
                        </span>
                      </div>
                    )}
                    
                    {formData.created_by && isHostConnected && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        ✓ Google Conectado
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    {!formData.created_by ? (
                      'Selecione um host primeiro'
                    ) : formData.created_by !== user?.id && !isHostConnected ? (
                      `⚠️ ${allUsers.find(u => u.id === formData.created_by)?.display_name || 'O host'} precisa conectar o próprio Google Calendar para que os convites sejam enviados`
                    ) : sendGoogleInvite && isHostConnected ? (
                      '✓ Convites serão enviados pelo Google Calendar com verificação de disponibilidade'
                    ) : sendGoogleInvite && !isHostConnected ? (
                      '⚠️ Você precisa conectar seu Google Calendar primeiro'
                    ) : (
                      'Convites não serão enviados automaticamente'
                    )}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSubmitting || !formData.title || !formData.scheduled_at || !formData.created_by || (sendGoogleInvite && !isHostConnected)}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Agendando...
                    </>
                  ) : sendGoogleInvite && !isHostConnected ? (
                    <>
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Conecte o Google Calendar
                    </>
                  ) : (
                    'Schedule Meeting'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Upcoming Meetings */}
      {upcomingMeetings.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-3 text-muted-foreground">Upcoming</h4>
          <div className="space-y-3">
          {upcomingMeetings.map((meeting) => {
              const StatusIcon = statusIcons[meeting.status as keyof typeof statusIcons] || Clock;
              return (
                <Card key={meeting.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium">{meeting.title}</h4>
                          <Badge className={statusColors[meeting.status]}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {meeting.status}
                          </Badge>
                        </div>

                        <div className="space-y-1 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {safeFormat(new Date(meeting.scheduled_at), 'PPP')} at {safeFormat(new Date(meeting.scheduled_at), 'p')}
                          </div>
                          {meeting.location && (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              {meeting.location}
                            </div>
                          )}
                          {meeting.meeting_url && (
                            <div className="flex items-center gap-2">
                              <Video className="w-4 h-4" />
                              <a href={meeting.meeting_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                Join Meeting
                              </a>
                            </div>
                          )}
                          
                          {/* Host */}
                          {meeting.created_by && (
                            <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                              <User className="w-4 h-4" />
                              <span className="font-medium">Host:</span>
                              <span>
                                {allUsers.find(u => u.id === meeting.created_by)?.display_name || 
                                 allUsers.find(u => u.id === meeting.created_by)?.email ||
                                 'Unknown'}
                              </span>
                            </div>
                          )}
                          
                          {/* Attendees */}
                          {meeting.attendees && Array.isArray(meeting.attendees) && meeting.attendees.length > 0 && (
                            <div className="flex items-start gap-2">
                              <Users className="w-4 h-4 mt-0.5" />
                              <div className="flex-1">
                                <span className="font-medium">Participantes:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {meeting.attendees.map((email: string, idx: number) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                      {email}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {meeting.description && (
                          <p className="text-sm text-muted-foreground mt-2">{meeting.description}</p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setMeetingToReschedule(meeting);
                            setRescheduleFormData({
                              scheduled_at: meeting.scheduled_at.slice(0, 16),
                              location: meeting.location || '',
                              title: meeting.title,
                              description: meeting.description || '',
                            });
                            setRescheduleDialogOpen(true);
                          }}
                        >
                          <Calendar className="w-4 h-4 mr-1" />
                          Reagendar
                        </Button>
                        <Button size="sm" onClick={() => completeMeeting(meeting.id)}>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Complete
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={async () => {
                            const hasGoogleEvent = meeting.google_calendar_event_id;
                            const message = hasGoogleEvent 
                              ? 'Isso irá cancelar a reunião e remover do Google Calendar de todos os participantes.\n\nMotivo do cancelamento:'
                              : 'Motivo do cancelamento:';
                            
                            const reason = prompt(message);
                            if (reason) {
                              try {
                                await cancelMeeting(meeting.id, reason);
                              } catch (error) {
                                console.error('Error cancelling meeting:', error);
                              }
                            }
                          }}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Cancel Meeting
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Past Meetings */}
      {pastMeetings.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-3 text-muted-foreground">Past Meetings</h4>
          <div className="space-y-3">
            {pastMeetings.map((meeting) => {
              const StatusIcon = statusIcons[meeting.status as keyof typeof statusIcons] || Clock;
              return (
                <Card key={meeting.id} className="opacity-75">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-medium">{meeting.title}</h4>
                      <Badge className={statusColors[meeting.status]}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {meeting.status}
                      </Badge>
                      {meeting.status === 'scheduled' && (
                        <Badge variant="outline" className="ml-auto">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Ação Necessária
                        </Badge>
                      )}
                    </div>

                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {safeFormat(new Date(meeting.scheduled_at), 'PPP')}
                      </div>
                      
                      {/* Host */}
                      {meeting.created_by && (
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                          <User className="w-4 h-4" />
                          <span className="font-medium">Host:</span>
                          <span>
                            {allUsers.find(u => u.id === meeting.created_by)?.display_name || 
                             allUsers.find(u => u.id === meeting.created_by)?.email ||
                             'Unknown'}
                          </span>
                        </div>
                      )}
                      
                      {/* Attendees */}
                      {meeting.attendees && Array.isArray(meeting.attendees) && meeting.attendees.length > 0 && (
                        <div className="flex items-start gap-2">
                          <Users className="w-4 h-4 mt-0.5" />
                          <div className="flex-1">
                            <span className="font-medium">Participantes:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {meeting.attendees.map((email: string, idx: number) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {email}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Recording Link */}
                    {meeting.meeting_url && meeting.meeting_url.includes('daily.co') && (
                      <div className="mt-2 pt-2 border-t flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            // Open tab BEFORE await to avoid popup blocker
                            const win = window.open('', '_blank');
                            try {
                              // Extract room name from URL
                              let roomName = '';
                              try {
                                const urlObj = new URL(meeting.meeting_url!);
                                roomName = urlObj.pathname.replace(/^\//, '');
                              } catch {
                                const urlParts = meeting.meeting_url!.split('/');
                                roomName = urlParts[urlParts.length - 1];
                              }
                              if (!roomName) { win?.close(); return; }

                              // 1) Try RPC
                              const { data } = await supabase.rpc('get_meeting_recording_url', {
                                p_room_name: roomName,
                              });
                              const row = Array.isArray(data) ? data[0] : data;
                              let url = row?.recording_url;

                              // 2) Fallback: metadata on the call itself
                              if (!url && meeting.metadata?.daily_recording_url) {
                                url = meeting.metadata.daily_recording_url;
                              }

                              // 3) Fallback: refresh via daily_recording_id
                              if (!url && row?.daily_recording_id) {
                                const { data: freshLink } = await supabase.functions.invoke('get-daily-recording-access-link', {
                                  body: { recording_id: row.daily_recording_id },
                                });
                                url = freshLink?.download_link;
                              }

                              // 4) Fallback: search by room_name via edge function
                              if (!url) {
                                const { data: freshLink } = await supabase.functions.invoke('get-daily-recording-access-link', {
                                  body: { room_name: roomName },
                                });
                                url = freshLink?.download_link;
                              }

                              if (url && win) {
                                win.location.href = url;
                              } else {
                                win?.close();
                                toast.info('Gravação ainda não disponível');
                              }
                            } catch {
                              win?.close();
                              toast.error('Erro ao buscar gravação');
                            }
                          }}
                        >
                          <Video className="w-3 h-3 mr-1" />
                          Vídeo
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            let roomName = '';
                            try {
                              const urlObj = new URL(meeting.meeting_url!);
                              roomName = urlObj.pathname.replace(/^\//, '');
                            } catch {
                              const urlParts = meeting.meeting_url!.split('/');
                              roomName = urlParts[urlParts.length - 1];
                            }
                            if (!roomName) return;
                            const { data } = await supabase.rpc('get_meeting_recording_url', {
                              p_room_name: roomName,
                            });
                            const row = Array.isArray(data) ? data[0] : data;
                            if ((row as any)?.audio_url) {
                              window.open((row as any).audio_url, '_blank');
                            } else {
                              toast.info('Áudio MP3 ainda não disponível. O processamento pode levar alguns minutos.');
                            }
                          }}
                        >
                          <Play className="w-3 h-3 mr-1" />
                          Áudio (MP3)
                        </Button>
                      </div>
                    )}

                    {/* AI Transcript */}
                    {meeting.transcript && (
                      <div className="mt-2 pt-2 border-t">
                        <button
                          type="button"
                          className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full"
                          onClick={() => {
                            setExpandedTranscripts(prev => {
                              const next = new Set(prev);
                              if (next.has(meeting.id)) {
                                next.delete(meeting.id);
                              } else {
                                next.add(meeting.id);
                              }
                              return next;
                            });
                          }}
                        >
                          <FileText className="w-4 h-4" />
                          <Badge className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 text-blue-700 border-blue-200 text-xs">
                            <Sparkles className="w-3 h-3 mr-1" />
                            Transcrição IA
                          </Badge>
                          {expandedTranscripts.has(meeting.id) ? (
                            <ChevronUp className="w-4 h-4 ml-auto" />
                          ) : (
                            <ChevronDown className="w-4 h-4 ml-auto" />
                          )}
                        </button>
                        {expandedTranscripts.has(meeting.id) && (
                          <div className="mt-2 p-3 rounded-lg bg-gradient-to-br from-blue-50/50 to-indigo-50/50 border border-blue-100 max-h-80 overflow-y-auto">
                            <pre className="text-xs text-foreground whitespace-pre-wrap font-sans leading-relaxed">
                              {meeting.transcript}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action Buttons - only for scheduled meetings */}
                    {meeting.status === 'scheduled' && (
                      <div className="flex gap-2 mt-4 pt-4 border-t">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setMeetingToReschedule(meeting);
                            setRescheduleFormData({
                              scheduled_at: meeting.scheduled_at.slice(0, 16),
                              location: meeting.location || '',
                              title: meeting.title,
                              description: meeting.description || '',
                            });
                            setRescheduleDialogOpen(true);
                          }}
                        >
                          <Calendar className="w-4 h-4 mr-1" />
                          Reagendar
                        </Button>
                        <Button size="sm" onClick={() => completeMeeting(meeting.id)}>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Complete
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={async () => {
                            const hasGoogleEvent = meeting.google_calendar_event_id;
                            const message = hasGoogleEvent 
                              ? 'Isso irá cancelar a reunião e remover do Google Calendar de todos os participantes.\n\nMotivo do cancelamento:'
                              : 'Motivo do cancelamento:';
                            
                            const reason = prompt(message);
                            if (reason) {
                              try {
                                await cancelMeeting(meeting.id, reason);
                              } catch (error) {
                                console.error('Error cancelling meeting:', error);
                              }
                            }
                          }}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Cancel Meeting
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {meetings.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            No meetings scheduled yet. Click "Schedule Meeting" to create one.
          </CardContent>
        </Card>
      )}

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reagendar Reunião</DialogTitle>
          </DialogHeader>
          <form 
            onSubmit={async (e) => {
              e.preventDefault();
              if (!meetingToReschedule) return;
              
              try {
                await rescheduleMeeting(meetingToReschedule.id, {
                  ...rescheduleFormData,
                  scheduled_at: new Date(rescheduleFormData.scheduled_at).toISOString(),
                  duration_minutes: 60,
                });
                setRescheduleDialogOpen(false);
                setMeetingToReschedule(null);
              } catch (error) {
                console.error('Reschedule error:', error);
              }
            }} 
            className="space-y-4"
          >
            <div>
              <Label htmlFor="reschedule_title">Título</Label>
              <Input
                id="reschedule_title"
                value={rescheduleFormData.title}
                onChange={(e) => setRescheduleFormData({ 
                  ...rescheduleFormData, 
                  title: e.target.value 
                })}
                required
              />
            </div>

            <div>
              <Label htmlFor="reschedule_datetime">Nova Data e Hora *</Label>
              <Input
                id="reschedule_datetime"
                type="datetime-local"
                value={rescheduleFormData.scheduled_at}
                onChange={(e) => setRescheduleFormData({ 
                  ...rescheduleFormData, 
                  scheduled_at: e.target.value
                })}
                required
              />
            </div>

            <div>
              <Label htmlFor="reschedule_location">Local</Label>
              <Input
                id="reschedule_location"
                value={rescheduleFormData.location}
                onChange={(e) => setRescheduleFormData({ 
                  ...rescheduleFormData, 
                  location: e.target.value 
                })}
              />
            </div>

            <div>
              <Label htmlFor="reschedule_description">Descrição</Label>
              <Textarea
                id="reschedule_description"
                value={rescheduleFormData.description}
                onChange={(e) => setRescheduleFormData({ 
                  ...rescheduleFormData, 
                  description: e.target.value 
                })}
              />
            </div>

            {meetingToReschedule?.google_calendar_event_id && (
              <div className="bg-blue-50 p-3 rounded-md text-sm flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-blue-900">
                  Esta reunião está sincronizada com Google Calendar. Todos os participantes 
                  receberão um email de atualização automaticamente.
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1">
                <Calendar className="w-4 h-4 mr-2" />
                Reagendar
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setRescheduleDialogOpen(false);
                  setMeetingToReschedule(null);
                }}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      <MeetingConfirmationModal
        isOpen={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setIsDialogOpen(false);
        }}
        meeting={confirmedMeeting}
      />
    </div>
  );
}
